<!-- Main content container -->
<ng-container *appHasPermission="{ resource: Resources.MerchantWhitelistedWallets, scope: Scopes.Read }">
  <div class="whitelisted-wallets-container">
    @if (!showAddWalletForm) {
      <!-- Header -->
      <div class="header-controls">
        <div class="title-section">
          <div class="title-container">
            <span class="inner-tab-title">Whitelisted Addresses ({{ totalWhitelistedAddresses }})</span>
            <app-button
              *appHasPermission="{ resource: Resources.MerchantWhitelistedWallets, scope: Scopes.Create }"
              buttonVariant="accent-add"
              buttonType="button"
              (buttonClick)="onAddAddress()"
              >
              <mat-icon>add</mat-icon>
              <span>Add Address</span>
            </app-button>
          </div>
        </div>
        <div class="filter-buttons">
          <app-button buttonVariant="filter" buttonType="button" [active]="filtersApplied" (buttonClick)="toggleFilters()">
            <img [src]="'shared-assets/icons/filter.png'" alt="Filter Icon" class="filter-icon" />
            <span>Filters</span>
            @if (filtersApplied) {
              <span
                class="clear-icon"
                (click)="clearFilters(); $event.stopPropagation()"
                title="Clear filters"
                >
                <img src="shared-assets/icons/close.png" alt="Filter Icon" class="clear-icon" />
              </span>
            }
          </app-button>
          <app-pill-search
            [searchText]="searchText"
            placeholder="Search"
            (searchChange)="onSearchChange($event)"
            [isDisabled]="isDisabled"
          ></app-pill-search>
        </div>
      </div>
      <!-- Table -->
      <div class="table-container">
        @if (isLoading) {
          <div class="loading-container">
            <app-loading-spinner size="large" label="Loading whitelisted wallets..."></app-loading-spinner>
          </div>
        }
        @if (error && !isLoading) {
          <div class="error-container">
            <p class="error-message">{{ error }}</p>
            <button class="retry-button" (click)="loadWhitelistedWallets()">Retry</button>
          </div>
        }
        @if (!isLoading && !error) {
          <table>
            <thead>
              <tr>
                <th>Wallet Address</th>
                <th>Blockchain</th>
                <th>Wallet Name</th>
                <th>Prepared by</th>
                <th>Date</th>
                @if (isSettlementApp) {
                  <th>Withdrawal Type</th>
                  <th>Risk Level</th>
                }
                <th>Status</th>
              </tr>
            </thead>
            @if (filteredWallets.length > 0) {
              <tbody>
                @for (wallet of filteredWallets; track wallet.id) {
                  <tr
                    class="wallet-row"
                    (click)="showWhitelistedWallet(wallet)"
                    [class.selected]="selectedWallet?.id === wallet.id"
                    >
                    <td class="olv-address" [matTooltip]="wallet.walletAddress">
                      {{ shortenAddress(wallet.walletAddress) }}
                    </td>
                    <td>{{ wallet.blockchain }}</td>
                    <td>{{ wallet.walletName }}</td>
                    <td>{{ wallet.preparedBy }}</td>
                    <td>{{ formatDateStandard(wallet.date) }}</td>
                    @if (isSettlementApp) {
                      <td>{{ wallet.withdrawalType }}</td>
                      <td>
                        <div class="risk-container">
                          <app-state-badge type="risk" [value]="wallet.riskLevel"></app-state-badge>
                        </div>
                      </td>
                    }
                    <td>
                      <span class="status-text" [ngClass]="'status-' + wallet.status.toLowerCase()">
                        {{ wallet.status }}
                      </span>
                    </td>
                  </tr>
                }
              </tbody>
            } @else {
              @if (!isLoading) {
                <tr>
                  <td colspan="8">
                    <app-empty-state
                      [filtersApplied]="filtersApplied"
                      [searchText]="searchText"
                    ></app-empty-state>
                  </td>
                </tr>
              }
            }
          </table>
        }
        <app-show-more [isShowMore]="hasMoreWallets" [isLoading]="isMoreLoading" (loadMore)="loadMoreWallets()">
        </app-show-more>
      </div>
    } @else {
      <app-form-container heading="Whitelist Wallet" [formGroup]="addWalletForm">
        <div class="form-group">
          <app-blockchain-dropdown
            label="Blockchain"
            placeholder="Select Blockchain"
            [options]="metadata.options()"
            formControlName="blockchain"
            [isInvalid]="getFieldError('blockchain', 'Blockchain') !== ''"
            [errorMessage]="getFieldError('blockchain', 'Blockchain')"
            />
        </div>
        <div class="form-group">
          <app-text-input
            label="Recipient Address"
            placeholder="Add address"
            formControlName="recipientAddress"
            [isInvalid]="
              getFieldError('recipientAddress', 'Recipient address') !== '' || !!backendErrors['recipientAddress']
            "
            [errorMessage]="backendErrors['recipientAddress'] || getFieldError('recipientAddress', 'Recipient address')"
            >
          </app-text-input>
        </div>
        @if (isSettlementApp) {
          <div class="form-group">
            <app-dropdown
              label="Whitelisted Wallet Type"
              placeholder="Select Whitelisted Wallet Type"
              [options]="whitelistedWalletTypeOptions"
              formControlName="whitelistedWalletType"
              [isInvalid]="getFieldError('whitelistedWalletType', 'Whitelisted Wallet Type') !== ''"
              [errorMessage]="getFieldError('whitelistedWalletType', 'Whitelisted Wallet Type')"
              />
          </div>
        }
        <div class="form-group">
          <app-text-input
            label="Name"
            placeholder="Wallet Name"
            formControlName="walletName"
            [isInvalid]="getFieldError('walletName', 'Wallet name') !== '' || !!backendErrors['walletName']"
            [errorMessage]="backendErrors['walletName'] || getFieldError('walletName', 'Wallet name')"
            >
          </app-text-input>
        </div>
        <div class="form-group">
          <label for="fileInput">Attach Document</label>
          <div class="attachment-area">
            @if (!attachedFileName) {
              <label for="fileInput" class="attach-button">
                <mat-icon>add</mat-icon>
                <span>Add attachment</span>
              </label>
              <input
                id="fileInput"
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                (change)="onFileSelected($event)"
                hidden
                />
            }
            @if (attachedFileName) {
              <div
                class="file-chip"
                [ngClass]="{ 'invalid-chip': getFieldError('attachedDocument', 'Document') !== '' }"
                >
                <mat-icon class="file-icon-chip"> insert_drive_file</mat-icon>
                <span>{{ attachedFileName }}</span>
                <button type="button" class="remove-file-btn" (click)="removeAttachedFile()">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
          @if (getFieldError('attachedDocument', 'Document') !== '') {
            <div class="error-message">
              <mat-icon class="error-icon">error_outline</mat-icon>
              <span>{{ getFieldError('attachedDocument', 'Document') }}</span>
            </div>
          }
        </div>
        <div class="form-actions">
          <app-button buttonVariant="medium-secondary" buttonType="button" (buttonClick)="onCancelAddWallet()">
            Cancel
          </app-button>
          <app-button
            buttonVariant="medium-primary"
            buttonType="button"
            [state]="addWalletNextButtonState"
            (buttonClick)="openOTP()"
            >
            Next
          </app-button>
        </div>
      </app-form-container>
    }

    <!-- whitelisted wallet details aside -->
    @if (sidebarOpen && selectedWallet) {
      <div class="details-aside" animate.enter="slide-in-right" animate.leave="slide-out-right">
        <app-merchant-whitelisted-wallet-details
          [wallet]="selectedWallet"
          (closed)="closeWalletDetails()"
          (refresh)="onWalletUpdated()"
        ></app-merchant-whitelisted-wallet-details>
      </div>
    }
  </div>
</ng-container>
